import React, { useState, useEffect, useRef } from 'react';
import { MapPin, ExternalLink, PersonStanding, Train, Car, ChevronDown } from 'lucide-react';

const MAP_EMBED = 'https://maps.google.com/maps?q=5+Albert+Walk,+Bray,+Co.+Wicklow,+Ireland&output=embed&hl=en&z=17';
const MAP_LINK = 'https://maps.app.goo.gl/VEJMRaV6Bnnh7pMV7?g_st=am';

const directions = [
  {
    icon: PersonStanding,
    mode: 'Walking',
    duration: '~5–10 min from Bray Main Street',
    steps: [
      'Walk from Bray Main Street toward the seafront/station area',
      'Turn right at the Dart Station',
      'Continue straight to Albert Walk',
      'Mynd is at the beginning of Albert Walk on the right',
    ],
  },
  {
    icon: Train,
    mode: 'Public Transport',
    duration: '~45–60 min from Dublin city centre',
    steps: [
      'Take the DART southbound from Pearse / Tara Street / Connolly',
      'Travel to Bray Daly',
      'Exit Bray DART Station and turn left onto Albert Walk',
      'Mynd is at the beginning of Albert Walk on the right',
    ],
  },
  {
    icon: Car,
    mode: 'By Car',
    duration: 'From Dublin',
    steps: [
      'Drive south via the N11 to Bray exit',
      'Follow signs for Bray town centre / seafront / DART station',
      'Park near Bray DART Station area',
      'Mynd is at the beginning of Albert Walk on the right',
    ],
  },
];

function DirectionAccordion({ icon: Icon, mode, duration, steps }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#D1D8D0] rounded-2xl overflow-hidden bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-[#F2F4ED] transition-colors"
        data-testid={`directions-${mode.toLowerCase().replace(/ /g, '-')}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#E6EBE4] flex items-center justify-center flex-shrink-0">
            <Icon size={16} strokeWidth={1.5} className="text-[#708A72]" />
          </div>
          <div className="text-left">
            <p className="font-body font-semibold text-sm text-[#2C362A]">{mode}</p>
            <p className="font-body text-xs text-[#5B6E56]">{duration}</p>
          </div>
        </div>
        <ChevronDown
          size={16}
          strokeWidth={1.5}
          className={`text-[#708A72] transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-6 pb-5 border-t border-[#D1D8D0]/50">
          <ol className="mt-3 space-y-2">
            {steps.map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#E6EBE4] flex items-center justify-center font-body text-xs font-semibold text-[#708A72] mt-0.5">
                  {i + 1}
                </span>
                <span className="font-body text-sm text-[#5B6E56] leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

export default function Location() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible')),
      { threshold: 0.1 }
    );
    const el = sectionRef.current;
    if (el) el.querySelectorAll('.reveal').forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="location"
      className="py-24 lg:py-32 bg-white"
      data-testid="location-section"
      ref={sectionRef}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12 reveal">
          <h2 className="font-heading text-4xl md:text-5xl text-[#2C362A] leading-tight mb-3">
            Finding Us
          </h2>
          <div className="flex items-center gap-2 text-[#5B6E56]">
            <MapPin size={15} strokeWidth={1.5} />
            <span className="font-body text-base">Mynd, 5 Albert Walk, Bray, Co. Wicklow, A98 HP71</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Map */}
          <div className="reveal">
            <div className="rounded-[2rem] overflow-hidden border border-[#D1D8D0] shadow-sm aspect-[4/3]">
              <iframe
                title="Donna Gordon Therapy Location"
                src={MAP_EMBED}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="map-iframe"
              />
            </div>
            <div className="mt-4 flex justify-center">
              <a
                href={MAP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-[#708A72] text-[#708A72] font-body text-sm px-6 py-2.5 rounded-full hover:bg-[#E6EBE4] transition-all duration-300"
                data-testid="view-on-google-maps-btn"
              >
                <MapPin size={14} strokeWidth={1.5} />
                View on Google Maps
                <ExternalLink size={13} strokeWidth={1.5} />
              </a>
            </div>
          </div>

          {/* Directions */}
          <div className="reveal space-y-3">
            <p className="font-body text-sm font-semibold text-[#2C362A] mb-4">Getting Here</p>
            {directions.map((d) => (
              <DirectionAccordion key={d.mode} {...d} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
